import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Phone, Lock, LogIn } from 'lucide-react';
import axios from 'axios';

/* =========================
   UTILITÁRIOS DE TELEFONE
========================= */
const onlyNumbers = (value = '') => value.replace(/\D/g, '');

const formatPhoneBR = (value = '') => {
  const numbers = onlyNumbers(value);

  if (numbers.length <= 10) {
    return numbers.replace(
      /(\d{2})(\d{4})(\d{0,4})/,
      '($1) $2-$3'
    );
  }

  return numbers.replace(
    /(\d{2})(\d{5})(\d{0,4})/,
    '($1) $2-$3'
  );
};

const Login = () => {
  const [formData, setFormData] = useState({
    phone: '',   // sempre SEM máscara
    password: ''
  });

  const [loading, setLoading] = useState(false);
  const { login, API, siteSettings } = useContext(AuthContext);
  const navigate = useNavigate();

  /* ===== SUBMIT ===== */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const hasLetters = /[a-zA-Z]/.test(formData.phone);

    try {
      const payload = {
        phone: hasLetters ? formData.phone.trim() : onlyNumbers(formData.phone), // ignora máscara
        password: formData.password
      };

      const response = await axios.post(`${API}/auth/login`, payload);
      const { access_token, user } = response.data;

      login(access_token, user);
      toast.success('Login realizado com sucesso!');

      navigate(user.user_type === 'produtor' ? '/produtor' : '/marketplace');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  /* ===== INPUTS ===== */
 const handleChange = (e) => {
  const { name, value } = e.target;

  if (name === 'phone') {
    // Se for login admin (letras), não formata nem remove nada.
    // Se for telefone, permite máscara (visual) mas mantém só números/format.
    const hasLetters = /[a-zA-Z]/.test(value);

    setFormData(prev => ({
      ...prev,
      phone: hasLetters ? value : formatPhoneBR(value)
    }));
    return;
  }

  setFormData(prev => ({
    ...prev,
    [name]: value
  }));
};

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full space-y-8 animate-fade-in">
        <div className="text-center">
          <h2 className="text-4xl font-bold text-green-600 mb-2">Entrar</h2>
          <p className="text-gray-600">
            Acesse sua conta na {siteSettings?.site_name || 'Nossa Feira'}
          </p>
        </div>

        <Card className="card-feira">
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-2 text-2xl text-green-600">
              <LogIn className="w-6 h-6" />
              Bem-vindo de volta!
            </CardTitle>
            <CardDescription>
              Digite suas credenciais para acessar sua conta
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* TELEFONE */}
              <div className="space-y-2">
                <Label htmlFor="phone">Telefone / WhatsApp</Label>
                <div className="relative flex items-center">
                  <Phone className="absolute left-3 h-4 w-4 text-gray-500 pointer-events-none" />
                  <Input
                    id="phone"
                    name="phone"
                    type="text"
                    inputMode="text"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    required
                    placeholder="Digite seu usuário ou telefone"
                    value={formData.phone} // 👁️ VISUAL
                    onChange={handleChange}
                    className="pl-10 input-feira"
                  />
                </div>
              </div>

              {/* SENHA */}
              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <div className="relative flex items-center">
                  <Lock className="absolute left-3 h-4 w-4 text-gray-500 pointer-events-none" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    required
                    placeholder="Digite sua senha"
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10 input-feira"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-feira mt-6"
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-3">
              <Link
                to="/forgot-password"
                className="text-sm text-green-600 underline"
              >
                Esqueci minha senha
              </Link>

              <p className="text-sm text-gray-600">
                Ainda não tem conta?{' '}
                <Link
                  to="/register"
                  className="text-green-600 font-medium underline"
                >
                  Cadastre-se aqui
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
